import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Users, Search, Phone, Mail, Building2, Gamepad2, Trophy, Ticket, Calendar, CheckCircle2, XCircle, RotateCcw, MapPin, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/csvExport";

const ALL = "__all__";

export default function AdminCustomersPage({ superadmin = false }) {
  const [vendors, setVendors] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [vendorId, setVendorId] = useState(ALL);
  const [campaignId, setCampaignId] = useState(ALL);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Summary stats are fetched separately from an uncapped
  // aggregation endpoint. Deriving them from `items` alone froze
  // the KPI cards at the pagination limit (e.g. Unique Customers
  // was stuck at 500 while the true count was 579).
  const [statsData, setStatsData] = useState({
    unique_customers: 0,
    total_spins: 0,
    repeat_players: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [activePhone, setActivePhone] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Load vendors once
  useEffect(() => {
    (async () => {
      try {
        const r = await api.adminVendors();
        setVendors(r.data || []);
      } catch (e) {
        toast.error("Failed to load vendors");
      }
    })();
  }, []);

  // Load campaigns whenever vendor changes
  useEffect(() => {
    (async () => {
      if (vendorId === ALL) {
        setCampaigns([]);
        setCampaignId(ALL);
        return;
      }
      try {
        const r = await api.adminVendorCampaigns(vendorId);
        setCampaigns(r.data || []);
        setCampaignId(ALL);
      } catch (e) {
        toast.error("Failed to load campaigns");
      }
    })();
  }, [vendorId]);

  // Load customers with current filters (debounced for search).
  // Also refreshes the KPI strip from the uncapped /stats endpoint in
  // parallel — both use the SAME filter params so the summary stays
  // coherent with the visible table.
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      setStatsLoading(true);
      const params = {};
      if (vendorId !== ALL) params.vendor_id = vendorId;
      if (campaignId !== ALL) params.campaign_id = campaignId;
      if (q.trim()) params.q = q.trim();
      // Fire list + stats concurrently so the KPI numbers land at
      // roughly the same time as the table rather than after a
      // second round-trip.
      const listPromise = api.adminCustomers({ ...params, limit: 500 })
        .then((r) => setItems(r.data || []))
        .catch(() => toast.error("Failed to load customers"))
        .finally(() => setLoading(false));
      const statsPromise = api.adminCustomersStats(params)
        .then((r) => setStatsData(r.data || {
          unique_customers: 0, total_spins: 0, repeat_players: 0,
        }))
        .catch(() => {
          // Non-fatal — table still renders. Reset to zero so a
          // stale value doesn't mislead if the request fails.
          setStatsData({ unique_customers: 0, total_spins: 0, repeat_players: 0 });
        })
        .finally(() => setStatsLoading(false));
      await Promise.all([listPromise, statsPromise]);
    }, 250);
    return () => clearTimeout(t);
  }, [vendorId, campaignId, q]);

  const openDetail = async (phone) => {
    setActivePhone(phone);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await api.adminCustomerByPhone(phone);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load customer");
      setActivePhone(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setActivePhone(null);
    setDetail(null);
  };

  const resetFilters = () => {
    setVendorId(ALL);
    setCampaignId(ALL);
    setQ("");
  };

  const stats = useMemo(() => ({
    uniqueCustomers: Number(statsData.unique_customers || 0),
    totalSpins: Number(statsData.total_spins || 0),
    multiCampaign: Number(statsData.repeat_players || 0),
  }), [statsData]);

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-customers-page">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-3xl font-semibold flex items-center gap-3">
          <Users className="h-7 w-7 text-primary" />
          Customers
        </h1>
        <p className="text-muted-foreground text-sm">
          Every end‑user who played a wheel campaign — across {superadmin ? "the entire platform" : "all vendors"}.
          Filter by vendor & campaign, or search by phone.
        </p>
      </div>

      {/* KPI strip — uncapped totals from /admin/customers/stats. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5" data-testid="customers-kpi-unique">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Unique customers</div>
          {statsLoading ? (
            <Skeleton className="h-9 w-24 mt-1" />
          ) : (
            <div className="font-heading text-3xl font-semibold mt-1">{stats.uniqueCustomers.toLocaleString()}</div>
          )}
        </Card>
        <Card className="p-5" data-testid="customers-kpi-spins">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total spins</div>
          {statsLoading ? (
            <Skeleton className="h-9 w-24 mt-1" />
          ) : (
            <div className="font-heading text-3xl font-semibold mt-1">{stats.totalSpins.toLocaleString()}</div>
          )}
        </Card>
        <Card className="p-5" data-testid="customers-kpi-repeat">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Repeat players (≥ 2 campaigns)</div>
          {statsLoading ? (
            <Skeleton className="h-9 w-24 mt-1" />
          ) : (
            <div className="font-heading text-3xl font-semibold mt-1">{stats.multiCampaign.toLocaleString()}</div>
          )}
        </Card>
      </div>

      {/* Filter bar */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Vendor</label>
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger data-testid="customers-vendor-select">
                <SelectValue placeholder="All vendors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All vendors</SelectItem>
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.business} {v.city ? `· ${v.city}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-4">
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Campaign</label>
            <Select
              value={campaignId}
              onValueChange={setCampaignId}
              disabled={vendorId === ALL}
            >
              <SelectTrigger data-testid="customers-campaign-select">
                <SelectValue placeholder={vendorId === ALL ? "Select a vendor first" : "All campaigns"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All campaigns</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} {c.theme ? `· ${c.theme}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Search</label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Phone, name, email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="customers-search-input"
              />
            </div>
          </div>

          <div className="md:col-span-1 flex md:justify-end gap-2">
            {superadmin && (
              <Button
                variant="outline"
                onClick={() =>
                  downloadCsv({
                    url: "/superadmin/customers/export.csv",
                    params: {
                      ...(vendorId && vendorId !== ALL ? { vendor_id: vendorId } : {}),
                      ...(campaignId && campaignId !== ALL ? { campaign_id: campaignId } : {}),
                      ...(q ? { q } : {}),
                      limit: 1000,
                    },
                    filename: "citydooh-customers.csv",
                  })
                }
                data-testid="admin-customers-export-csv"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Export CSV
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={resetFilters}
              title="Reset filters"
              data-testid="customers-reset-filters"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Customer table */}
      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-secondary/40">
                  <th className="py-3 px-4">Customer</th>
                  <th className="py-3 px-4">Phone</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4 text-center">Campaigns</th>
                  <th className="py-3 px-4 text-center">Spins</th>
                  <th className="py-3 px-4">Last activity</th>
                  <th className="py-3 px-4">Last campaign</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr
                    key={c.phone}
                    className="border-t border-border hover:bg-secondary/40 transition-colors"
                    data-testid="customer-row"
                  >
                    <td className="py-3 px-4 font-medium">{c.name || "—"}</td>
                    <td className="py-3 px-4 font-mono text-xs">{c.phone}</td>
                    <td className="py-3 px-4 text-muted-foreground">{c.email || "—"}</td>
                    <td className="py-3 px-4 text-center">
                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/40">
                        {c.campaigns_count}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-center text-muted-foreground">{c.total_spins}</td>
                    <td className="py-3 px-4 text-muted-foreground">{fmt(c.last_spun_at)}</td>
                    <td className="py-3 px-4">
                      <div className="text-foreground/90">{c.last_campaign_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{c.last_vendor_business || ""}</div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetail(c.phone)}
                        data-testid="customer-view-button"
                      >
                        View profile
                      </Button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td className="py-10 text-center text-muted-foreground" colSpan="8" data-testid="customers-empty">
                      No customers match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detail dialog */}
      <Dialog open={!!activePhone} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="customer-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Customer profile
            </DialogTitle>
            <DialogDescription>
              Full participation history across all vendors & campaigns.
            </DialogDescription>
          </DialogHeader>

          {detailLoading || !detail ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <div className="space-y-5">
              {/* Profile header */}
              <Card className="p-5 bg-secondary/40">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium" data-testid="customer-profile-name">
                      {detail.profile.name || "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="font-mono text-xs" data-testid="customer-profile-phone">
                      {detail.profile.phone}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{detail.profile.email || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      First seen: {fmt(detail.profile.first_seen)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                  <Stat label="Total spins" value={detail.profile.total_spins} />
                  <Stat label="Campaigns" value={detail.profile.unique_campaigns} />
                  <Stat label="Coupons" value={detail.profile.coupons_received} />
                  <Stat label="Redeemed" value={detail.profile.coupons_redeemed} highlight />
                </div>
              </Card>

              {/* Participation history */}
              <div>
                <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
                  <Gamepad2 className="h-4 w-4 text-primary" />
                  Participation history ({detail.history.length})
                </h3>
                <div className="space-y-3">
                  {detail.history.map((h) => (
                    <Card
                      key={h.lead_id}
                      className="p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3"
                      data-testid="customer-history-row"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{h.campaign_name || "Campaign"}</span>
                          {h.campaign_theme && (
                            <Badge variant="outline" className="capitalize text-[10px]">
                              {h.campaign_theme}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                          <Building2 className="h-3 w-3" />
                          {h.vendor_business || "—"}
                          {h.vendor_city && (
                            <>
                              <MapPin className="h-3 w-3 ml-2" />
                              {h.vendor_city}
                            </>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                          <Calendar className="h-3 w-3" />
                          {fmt(h.spun_at)}
                        </div>
                      </div>

                      <div className="flex flex-col items-start md:items-end gap-1.5 min-w-[200px]">
                        <div className="flex items-center gap-1.5 text-sm">
                          <Trophy className="h-4 w-4 text-primary" />
                          <span className={h.is_better_luck ? "text-muted-foreground" : "font-medium"}>
                            {h.won_gift_name || h.won_label || "—"}
                          </span>
                        </div>
                        {h.coupon ? (
                          <div className="flex items-center gap-2 text-xs">
                            <Ticket className="h-3.5 w-3.5 text-primary" />
                            <span className="font-mono">{h.coupon.code}</span>
                            {h.coupon.is_redeemed ? (
                              <Badge variant="outline" className="text-emerald-400 border-emerald-400/40 bg-emerald-500/10">
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Redeemed
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-400 border-amber-400/40 bg-amber-500/10">
                                <XCircle className="h-3 w-3 mr-1" /> Unredeemed
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">No coupon issued</span>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div className="rounded-md bg-background border border-border px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-heading text-xl font-semibold ${highlight ? "text-primary" : ""}`}>
        {value ?? 0}
      </div>
    </div>
  );
}
